import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  FaCalendarAlt,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

export type DatePickerVariant = "header" | "light";

export function formatDisplayDate(iso: string) {
  const [yyyy, mm, dd] = iso.split("-");
  if (!yyyy || !mm || !dd) return iso || "Select date";
  return `${dd}-${mm}-${yyyy}`;
}

export function formatDisplayMonth(ym: string) {
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym || "Select month";
  return `${MONTHS[m - 1]} ${y}`;
}

function parseIsoToDate(iso: string): Date {
  if (!iso) return new Date();
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
}

function parseYearMonth(ym: string): Date {
  if (!ym) return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  return new Date(y, m - 1, 1);
}

function dateToIso(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function yearMonthOf(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const ACCENT = "#345952";

function useDismiss(
  open: boolean,
  onClose: () => void,
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef, panelRef]);
}

/** Fixed portal panel so parent overflow (e.g. overflow-x-hidden) cannot clip months. */
function useAnchoredPanelStyle(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  estimatedHeight: number,
) {
  const [style, setStyle] = useState<CSSProperties>({ visibility: "hidden" });

  useLayoutEffect(() => {
    if (!open) {
      setStyle({ visibility: "hidden" });
      return;
    }

    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const width = Math.min(
        Math.max(rect.width, 300),
        Math.max(160, viewportWidth - 16),
      );
      let left = rect.left;
      if (left + width > viewportWidth - 8) left = rect.right - width;
      left = Math.min(Math.max(8, left), Math.max(8, viewportWidth - width - 8));
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

      if (openUp) {
        setStyle({
          position: "fixed",
          left,
          width,
          bottom: window.innerHeight - rect.top + 8,
          top: "auto",
          zIndex: 1000,
          maxHeight: Math.max(160, spaceAbove),
          overflowY: "auto",
          visibility: "visible",
        });
      } else {
        setStyle({
          position: "fixed",
          left,
          width,
          top: rect.bottom + 8,
          bottom: "auto",
          zIndex: 1000,
          maxHeight: Math.max(160, spaceBelow),
          overflowY: "auto",
          visibility: "visible",
        });
      }
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, anchorRef, estimatedHeight]);

  return style;
}

function TriggerShell({
  variant,
  open,
  onClick,
  disabled,
  icon,
  label,
  value,
  className = "",
}: {
  variant: DatePickerVariant;
  open: boolean;
  onClick: () => void;
  disabled?: boolean;
  icon: ReactNode;
  label?: string;
  value: string;
  className?: string;
}) {
  const isHeader = variant === "header";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 min-w-0 w-full text-left transition-all disabled:opacity-50 ${
        isHeader
          ? `bg-white/10 hover:bg-white/15 border ${
              open ? "border-white/40 ring-2 ring-white/20" : "border-white/10"
            }`
          : `bg-white border shadow-sm hover:border-[#93B9AD]/50 ${
              open
                ? "border-[#345952] ring-2 ring-[#345952]/20"
                : "border-[#E8E8E4]"
            }`
      } ${className}`}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
          isHeader ? "bg-white/10 text-white/90" : "text-white"
        }`}
        style={isHeader ? undefined : { backgroundColor: ACCENT }}
      >
        {icon}
      </span>
      <span className="flex flex-col min-w-0 flex-1">
        {label ? (
          <span
            className={`text-[10px] uppercase tracking-wider font-semibold ${
              isHeader ? "text-white/60" : "text-[#8A8A85]"
            }`}
          >
            {label}
          </span>
        ) : null}
        <span
          className={`text-sm font-semibold truncate ${
            isHeader ? "text-white" : "text-[#4A4A48]"
          }`}
        >
          {value}
        </span>
      </span>
      <FaChevronDown
        className={`shrink-0 text-xs transition-transform ${open ? "rotate-180" : ""} ${
          isHeader ? "text-white/70" : "text-[#8A8A85]"
        }`}
      />
    </button>
  );
}

export function DropdownPortal({
  open,
  anchorRef,
  panelRef,
  estimatedHeight,
  children,
}: {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  estimatedHeight: number;
  children: ReactNode;
}) {
  const style = useAnchoredPanelStyle(open, anchorRef, estimatedHeight);
  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={panelRef}
      className="overflow-hidden rounded-2xl border border-[#E8E8E4] bg-white shadow-xl"
      style={{ ...style, boxShadow: "0 12px 40px rgba(0,0,0,0.12)" }}
    >
      {children}
    </div>,
    document.body,
  );
}

function CalendarGrid({
  viewMonth,
  selectedIso,
  minIso,
  maxIso,
  onSelect,
}: {
  viewMonth: Date;
  selectedIso: string;
  minIso?: string;
  maxIso?: string;
  onSelect: (iso: string) => void;
}) {
  const selected = parseIsoToDate(selectedIso || dateToIso(new Date()));
  const min = minIso ? parseIsoToDate(minIso) : null;
  const max = maxIso ? parseIsoToDate(maxIso) : null;

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const isDisabled = (day: Date) => {
    const t = startOfDay(day);
    if (min && t < startOfDay(min)) return true;
    if (max && t > startOfDay(max)) return true;
    return false;
  };

  return (
    <div className="p-3">
      <div className="mb-2 grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            className="py-1 text-center text-[10px] font-semibold uppercase text-[#8A8A85]"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const disabled = isDisabled(day);
          const active = selectedIso ? isSameDay(day, selected) : false;
          const iso = dateToIso(day);
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(iso)}
              className={`h-9 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? "bg-[#345952] text-white shadow-sm"
                  : disabled
                    ? "cursor-not-allowed text-[#D0D0CB]"
                    : "text-[#4A4A48] hover:bg-[#FDFBF2]"
              }`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CustomDatePicker({
  value,
  onChange,
  min,
  max,
  label,
  variant = "light",
  className = "",
  placeholder = "Select date",
  disabled = false,
}: {
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  label?: string;
  variant?: DatePickerVariant;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => parseIsoToDate(value));
  const close = useCallback(() => setOpen(false), []);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useDismiss(open, close, anchorRef, panelRef);

  useEffect(() => {
    if (open) setViewMonth(parseIsoToDate(value || dateToIso(new Date())));
  }, [open, value]);

  return (
    <div ref={anchorRef} className={`relative min-w-0 ${className}`}>
      <TriggerShell
        variant={variant}
        open={open}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
        icon={<FaCalendarAlt className="text-xs" />}
        label={label}
        value={value ? formatDisplayDate(value) : placeholder}
      />
      <DropdownPortal
        open={open}
        anchorRef={anchorRef}
        panelRef={panelRef}
        estimatedHeight={380}
      >
        <div className="flex items-center justify-between border-b border-[#E8E8E4] bg-[#FDFBF2] px-4 py-3">
          <button
            type="button"
            onClick={() =>
              setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))
            }
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-[#4A4A48] hover:border-[#E8E8E4] hover:bg-white"
            aria-label="Previous month"
          >
            <FaChevronLeft className="text-xs" />
          </button>
          <span className="text-sm font-semibold text-[#4A4A48]">
            {MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
          </span>
          <button
            type="button"
            onClick={() =>
              setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))
            }
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-[#4A4A48] hover:border-[#E8E8E4] hover:bg-white"
            aria-label="Next month"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
        <CalendarGrid
          viewMonth={viewMonth}
          selectedIso={value}
          minIso={min}
          maxIso={max}
          onSelect={(iso) => {
            onChange(iso);
            close();
          }}
        />
        <div className="flex items-center justify-between border-t border-[#E8E8E4] bg-[#FDFBF2] px-4 py-2">
          <button
            type="button"
            className="text-xs font-semibold text-[#345952]"
            onClick={() => {
              const today = dateToIso(new Date());
              if (min && today < min) return;
              if (max && today > max) return;
              onChange(today);
              close();
            }}
          >
            Today
          </button>
          <span className="text-sm font-semibold text-[#345952]">
            {value ? formatDisplayDate(value) : placeholder}
          </span>
        </div>
      </DropdownPortal>
    </div>
  );
}

export function CustomMonthPicker({
  value,
  onChange,
  min,
  max,
  label,
  variant = "light",
  className = "",
  placeholder = "Select month",
}: {
  value: string;
  onChange: (ym: string) => void;
  min?: string;
  max?: string;
  label?: string;
  variant?: DatePickerVariant;
  className?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = parseYearMonth(value);
  const [viewYear, setViewYear] = useState(() => selected.getFullYear());
  const close = useCallback(() => setOpen(false), []);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useDismiss(open, close, anchorRef, panelRef);

  useEffect(() => {
    if (open) setViewYear(parseYearMonth(value).getFullYear());
  }, [open, value]);

  const isDisabled = (monthIndex: number) => {
    const ym = `${viewYear}-${String(monthIndex + 1).padStart(2, "0")}`;
    if (min && ym < min) return true;
    if (max && ym > max) return true;
    return false;
  };

  return (
    <div ref={anchorRef} className={`relative min-w-0 ${className}`}>
      <TriggerShell
        variant={variant}
        open={open}
        onClick={() => setOpen((o) => !o)}
        icon={<FaCalendarAlt className="text-xs" />}
        label={label}
        value={value ? formatDisplayMonth(value) : placeholder}
      />
      <DropdownPortal
        open={open}
        anchorRef={anchorRef}
        panelRef={panelRef}
        estimatedHeight={320}
      >
        <div className="flex items-center justify-between border-b border-[#E8E8E4] bg-[#FDFBF2] px-4 py-3">
          <button
            type="button"
            onClick={() => setViewYear((y) => y - 1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-[#4A4A48] hover:border-[#E8E8E4] hover:bg-white"
            aria-label="Previous year"
          >
            <FaChevronLeft className="text-xs" />
          </button>
          <span className="text-sm font-semibold text-[#4A4A48]">{viewYear}</span>
          <button
            type="button"
            onClick={() => setViewYear((y) => y + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-[#4A4A48] hover:border-[#E8E8E4] hover:bg-white"
            aria-label="Next year"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2 p-3">
          {MONTHS.map((name, idx) => {
            const ym = `${viewYear}-${String(idx + 1).padStart(2, "0")}`;
            const active = value === ym;
            const disabled = isDisabled(idx);
            return (
              <button
                key={name}
                type="button"
                disabled={disabled}
                onClick={() => {
                  onChange(ym);
                  close();
                }}
                className={`rounded-xl px-2 py-2.5 text-sm font-medium transition ${
                  active
                    ? "bg-[#345952] text-white shadow-sm"
                    : disabled
                      ? "cursor-not-allowed text-[#D0D0CB]"
                      : "text-[#4A4A48] hover:bg-[#FDFBF2]"
                }`}
              >
                {name.slice(0, 3)}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between border-t border-[#E8E8E4] bg-[#FDFBF2] px-4 py-2">
          <button
            type="button"
            className="text-xs font-semibold text-[#345952]"
            onClick={() => {
              const now = yearMonthOf(new Date());
              if (min && now < min) return;
              if (max && now > max) return;
              onChange(now);
              close();
            }}
          >
            This month
          </button>
          <span className="text-sm font-semibold text-[#345952]">
            {value ? formatDisplayMonth(value) : placeholder}
          </span>
        </div>
      </DropdownPortal>
    </div>
  );
}
